import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { App } from "./App";
import { RouteError } from "./components/RouteError";
import { oneOf } from "./lib/search";
import { ActivitiesPage, validateActivitiesSearch } from "./pages/Activities";
import { ActivityAssignmentsPage, validateAssignmentsSearch } from "./pages/ActivityAssignments";
import { ActivityAdminPage } from "./pages/ActivityAdmin";
import { ActivityDetailPage } from "./pages/ActivityDetail";
import { ActivityReviewsPage } from "./pages/ActivityReviews";
import { ActivityCataloguePage, validateCatalogueSearch } from "./pages/ActivityCatalogue";
import { ActivityEditorPage } from "./pages/ActivityEditor";
import { CEMMIE_FILTERS, CeMMiesPage } from "./pages/CeMMies";
import { CeMMChatPage } from "./pages/CeMMChat";
import { DashboardPage } from "./pages/Dashboard";
import { FeedbackPage } from "./pages/Feedback";
import { EventsPage, validateEventsSearch } from "./pages/Events";
import { EventDetailPage } from "./pages/EventDetail";
import { SeminarPlanningPage, validateSeminarPlanningSearch } from "./pages/SeminarPlanning";
import { GrantsPage } from "./pages/Grants";
import { GROUP_FILTERS, GroupProfilePage, GroupsPage } from "./pages/Groups";
import { NotificationsPage, validateNotificationsSearch } from "./pages/Notifications";
import { CeMMieProfilePage, ProfilePage } from "./pages/Profile";
import { PeopleLeavePage } from "./pages/PeopleLeave";
import { ProgrammeMatrixPage, validateMatrixSearch } from "./pages/ProgrammeMatrix";
import { ProgrammeProgressPage } from "./pages/ProgrammeProgress";
import { ResearchOutputPage } from "./pages/ResearchOutput";
import { SupervisedStudentsPage } from "./pages/SupervisedStudents";
import { ReconciliationPage } from "./pages/Reconciliation";
import { AuditLogPage, validateAuditSearch } from "./pages/AuditLog";
import { AdministrationPage } from "./pages/Administration";
import { RolesPage } from "./pages/Roles";
import { AppFeedbackPage, validateAppFeedbackSearch } from "./pages/AppFeedback";

// The root renders its children and nothing else, so that ONE route can sit
// outside the sign-in. Everything else hangs off `authedRoute` below, whose
// component is the gate and the sidebar.
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Pathless: it contributes no URL segment, only the authenticated shell. The
// leading underscore is the convention for that, and it is why a `useParams`
// elsewhere says `from: "/_authed/groups/$shortName"` -- the ROUTE id gained a
// segment that the URL did not.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  component: App,
});

// The one un-gated page (model §14). It must stay a child of the ROOT: moved
// under the shell it would render the sidebar to a stranger and send them to
// Microsoft to sign in, which is the whole thing anonymous feedback is not.
const feedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/feedback/$token",
  component: FeedbackPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: DashboardPage,
});

// Specialised dashboards rather than activity pages: both answer "how is
// this going" and neither is a worklist, so they sit beside the landing page
// rather than under /activities.
const programmeProgressRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/my-programme",
  component: ProgrammeProgressPage,
});

const supervisedStudentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/my-group",
  component: SupervisedStudentsPage,
});

// My group for the other relationship: the students the viewer co-supervises.
const coSupervisionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/co-supervision",
  component: () => <SupervisedStudentsPage variant="coSupervision" />,
});

// The programme manager's view. Under the activity admin section rather than
// beside the personal dashboards: it is about a cohort, not about the viewer.
const programmeMatrixRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/admin/progress",
  validateSearch: validateMatrixSearch,
  component: ProgrammeMatrixPage,
});

// One person's programme, for a supervisor or the programme manager. The
// query decides who may look in -- an admin, or whoever supervises them -- so
// the route does not need a gate of its own.
const personProgrammeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/programme/$personId",
  // Where it was opened from, so the trail leads back there: a supervisor
  // comes from My group, and the matrix behind "Programme progress" would
  // refuse them.
  // Optional, so the matrix links here without saying anything.
  validateSearch: (search: Record<string, unknown>): { from?: "group" | "coSupervision" } =>
    search.from === "group" || search.from === "coSupervision" ? { from: search.from } : {},
  component: function PersonProgrammeRoute() {
    const { personId } = personProgrammeRoute.useParams();
    const { from } = personProgrammeRoute.useSearch();
    return <ProgrammeProgressPage personId={personId} from={from} />;
  },
});

// Leave sits under people, not under activities: it is a fact about
// somebody's employment that the activity module reacts to.
const peopleLeaveRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cemmies/leave",
  component: PeopleLeavePage,
});

const researchOutputRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/research-output",
  component: ResearchOutputPage,
});

const activitiesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities",
  validateSearch: validateActivitiesSearch,
  component: ActivitiesPage,
});

const activityAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/admin",
  component: ActivityAdminPage,
});

const activityCatalogueRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/admin/catalogue",
  validateSearch: validateCatalogueSearch,
  component: ActivityCataloguePage,
});

const activityEditorNewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/admin/catalogue/new",
  component: ActivityEditorPage,
  validateSearch: (search: Record<string, unknown>) => ({
    cloneFrom: typeof search.cloneFrom === "string" ? search.cloneFrom : undefined,
  }),
});

const activityEditorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/admin/catalogue/$activityId",
  component: ActivityEditorPage,
});

const activityAssignmentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/admin/assigned",
  validateSearch: validateAssignmentsSearch,
  component: ActivityAssignmentsPage,
});

const activityReviewsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/reviews",
  component: ActivityReviewsPage,
});

const personActivityDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/activities/$personActivityId",
  // Where it was opened from, so the trail and the back button lead there: a
  // reviewer comes from the review queue, not from their own activities.
  // Optional, so every other link says nothing and gets "Activities".
  validateSearch: (search: Record<string, unknown>): { from?: "reviews" | "dashboard" } =>
    search.from === "reviews" || search.from === "dashboard" ? { from: search.from } : {},
  component: ActivityDetailPage,
});

const cemmiesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cemmies",
  validateSearch: (search: Record<string, unknown>): { status?: (typeof CEMMIE_FILTERS)[number] } => ({
    status: oneOf(search.status, CEMMIE_FILTERS),
  }),
  component: CeMMiesPage,
});

const cemmChatRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cemm-chat",
  component: CeMMChatPage,
});

const cemmieProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cemmies/$shortName",
  component: CeMMieProfilePage,
});

const groupsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/groups",
  validateSearch: (search: Record<string, unknown>): { filter?: (typeof GROUP_FILTERS)[number] } => ({
    filter: oneOf(search.filter, GROUP_FILTERS),
  }),
  component: GroupsPage,
});

const groupProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/groups/$shortName",
  // The member list's sub-group filter: ?unit=grants is the Administration
  // page narrowed to Grants. Filters live in the query string, typed and
  // checked here, so that a filtered view can be linked to and survives a
  // reload; anything unrecognised falls back to no filter.
  validateSearch: (search: Record<string, unknown>): { unit?: string } =>
    typeof search.unit === "string" && search.unit !== "" ? { unit: search.unit } : {},
  component: GroupProfilePage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/notifications",
  validateSearch: validateNotificationsSearch,
  component: NotificationsPage,
});

const grantsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/grants",
  component: GrantsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/profile",
  component: ProfilePage,
});

const eventsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events",
  validateSearch: validateEventsSearch,
  component: EventsPage,
});

const seminarPlanningRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events/planning",
  validateSearch: validateSeminarPlanningSearch,
  component: SeminarPlanningPage,
});

const eventDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events/$eventId",
  component: EventDetailPage,
});

// Administration: HR's and IT's pages, each still gated on its own role. The
// section page lists whichever of them the viewer may open.
const administrationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin",
  component: AdministrationPage,
});

const reconciliationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin/reconciliation",
  component: ReconciliationPage,
});

// Sys admin only. The query refuses anybody else, which is what the error
// boundary then shows -- the same arrangement as the reconciliation report.
const rolesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin/roles",
  component: RolesPage,
});

const appFeedbackRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin/feedback",
  validateSearch: validateAppFeedbackSearch,
  component: AppFeedbackPage,
});

const auditLogRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin/audit-log",
  validateSearch: validateAuditSearch,
  component: AuditLogPage,
});

const routeTree = rootRoute.addChildren([
  feedbackRoute,
  authedRoute.addChildren([
    dashboardRoute,
    programmeProgressRoute,
    personProgrammeRoute,
    supervisedStudentsRoute,
    coSupervisionRoute,
    programmeMatrixRoute,
    researchOutputRoute,
    activitiesRoute,
    activityAdminRoute,
    activityCatalogueRoute,
    activityEditorNewRoute,
    activityEditorRoute,
    activityAssignmentsRoute,
    activityReviewsRoute,
    personActivityDetailRoute,
    cemmiesRoute,
    peopleLeaveRoute,
    cemmChatRoute,
    cemmieProfileRoute,
    groupsRoute,
    groupProfileRoute,
    notificationsRoute,
    grantsRoute,
    profileRoute,
    eventsRoute,
    seminarPlanningRoute,
    eventDetailRoute,
    administrationRoute,
    reconciliationRoute,
    auditLogRoute,
    rolesRoute,
    appFeedbackRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultStructuralSharing: true,
  // Convex's useQuery throws, so a refusal from the server reaches the router
  // as a render error. Without a component here the reader gets the raw text,
  // request id and all, which reads as a broken application rather than as a
  // door that is closed to them.
  defaultErrorComponent: ({ error, info }) => (
    <RouteError error={error} componentStack={info?.componentStack} />
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
